/**
 * In-context instructions — the state rules behind `<Instructions>`
 * (CONVERSIONS_SPEC.md §6, slice C2).
 *
 * <p>Everything here is a pure function over a storage interface, for the same reason
 * `journalMath.ts` exists: pure logic is cheaper to test exhaustively than a component, and
 * the React shell in `components/Instructions.tsx` stays thin — it renders, and it calls
 * these. <b>⚠ Corrected 2026-08-18:</b> this paragraph used to give the reason as <i>"vitest
 * runs in a node environment with no jsdom, so logic inside a component is logic no test can
 * reach"</i>. <b>That has been false since 2026-07-31</b>; the component is rendered under
 * jsdom in `components/Instructions.render.test.tsx`. The separation survives on its own
 * merits, without the false premise.</p>
 *
 * <p>⚠ <b>Failing open is the rule, in every direction.</b> Unreadable storage, an
 * unrecognised value and a browser with no storage at all all resolve to EXPANDED. The
 * audience for this panel is a bookkeeper who has never seen the tool; the cost of showing
 * instructions to someone who already dismissed them is an extra click, and the cost of
 * hiding them from someone who never did is the whole point of the slice.</p>
 */

/** The two `Storage` members used here — narrower than `Storage` so a test can supply one. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * Every screen that carries an instructions panel (§6: *"Every screen gets one"*).
 *
 * <p>This registry is what makes "every" checkable. `instructions.content.test.ts` walks it
 * and demands EN + ES content for each entry, so adding a screen here without writing its
 * copy fails the suite rather than shipping a panel that renders a raw key.</p>
 */
export const INSTRUCTION_SCREENS = [
  'bankTransactions',
  'accountTransactions',
  'manualJournals',
  'glMappings',
  'clientHistory',
  'uploads',
  'work',
] as const;

export type InstructionScreen = (typeof INSTRUCTION_SCREENS)[number];

const COLLAPSED = 'collapsed';
const EXPANDED = 'expanded';

/**
 * The browser's `localStorage`, or `undefined` where there isn't one.
 *
 * <p>⚠ Reached through a function rather than referenced directly in the component, and it
 * is <b>not</b> only about node tests. `window.localStorage` <i>itself</i> throws — not just
 * its methods — when a browser blocks storage access for the origin. Read at module scope
 * that is an exception before React renders, which is the precise shape of the 2026-07-31
 * white-screen (`bootError.ts` exists because of it).</p>
 */
export function browserStorage(): StorageLike | undefined {
  try {
    if (typeof window === 'undefined') return undefined;
    return window.localStorage ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Per-screen key. §6 requires dismissal to be per-screen rather than global, so the screen
 * name is IN the key — one shared key would make dismissing the journals panel also dismiss
 * the one on G/L mappings, which is the behaviour the spec explicitly rules out.
 */
export function instructionsStorageKey(screen: InstructionScreen): string {
  return `ptc-conversions-instructions-${screen}`;
}

/** Reads the persisted collapse state. Anything other than a stored `collapsed` is expanded. */
export function readCollapsed(
  storage: StorageLike | undefined,
  screen: InstructionScreen,
): boolean {
  if (!storage) return false;
  try {
    return storage.getItem(instructionsStorageKey(screen)) === COLLAPSED;
  } catch {
    // Safari private mode throws rather than returning null.
    return false;
  }
}

/**
 * Persists the collapse state. Expanded is written as an explicit value rather than by
 * removing the key, so "the user re-opened this" stays distinguishable from "never touched".
 */
export function writeCollapsed(
  storage: StorageLike | undefined,
  screen: InstructionScreen,
  collapsed: boolean,
): void {
  if (!storage) return;
  try {
    storage.setItem(instructionsStorageKey(screen), collapsed ? COLLAPSED : EXPANDED);
  } catch {
    // A browser that refuses to remember the choice must not break the screen carrying it.
  }
}

/**
 * The steps behind `instructions.<screen>.steps`, in order.
 *
 * <p>The locale files hold them as numbered keys (`"1"`, `"2"`, …) rather than an array, because
 * the fleet parity gate (`tools/i18n/check-parity.mjs`, ptc-statetax's, copied verbatim) refuses
 * non-string values — and an array is exactly where it would be blind: a Spanish list one step
 * short is a key-for-key match on its parent. As numbered keys, every step is a key parity checks.</p>
 *
 * <p>Sorted <b>numerically</b>, so a tenth step lands after the ninth rather than after the first.
 * Anything that is not an object of strings — the key missing in every locale, so i18next hands back
 * the key itself — yields no steps; `instructions.content.test.ts` is what keeps that unreachable.</p>
 */
export function stepsInOrder(raw: unknown): string[] {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return [];
  return Object.entries(raw as Record<string, unknown>)
    .filter((entry): entry is [string, string] => /^\d+$/.test(entry[0]) && typeof entry[1] === 'string')
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([, step]) => step);
}
